/**
 * Toasts, for outcomes that do not need an answer.
 *
 * An external store rather than a slice: a toast is transient UI with a timer, and putting it in
 * Redux would mean a reducer that schedules `setTimeout` — a side effect in the one place that
 * must not have them.
 */

import { uuidv7 } from '@pos/shared';

export type ToastTone = 'success' | 'danger' | 'warning' | 'info';

export type Toast = {
  readonly id: string;
  readonly tone: ToastTone;
  readonly message: string;
  /** Offered when the outcome is something the user can act on. */
  readonly action?: { readonly label: string; readonly run: () => void } | undefined;
};

const DURATION: Record<ToastTone, number> = {
  success: 2600,
  info: 3200,
  warning: 5000,
  // Long, because a failure at the counter has to survive the salesperson looking up at the
  // customer and back.
  danger: 7000,
};

let toasts: readonly Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, number>();

function publish(): void {
  for (const listener of listeners) listener();
}

export function subscribeToToasts(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const getToasts = (): readonly Toast[] => toasts;

export function dismissToast(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timers.delete(id);
  }

  toasts = toasts.filter((toast) => toast.id !== id);
  publish();
}

function show(tone: ToastTone, message: string, action?: Toast['action']): string {
  const id = uuidv7();
  toasts = [...toasts, { id, tone, message, action }];
  publish();

  timers.set(
    id,
    window.setTimeout(() => {
      dismissToast(id);
    }, DURATION[tone]),
  );

  return id;
}

export const toast = {
  success: (message: string, action?: Toast['action']) => show('success', message, action),
  info: (message: string, action?: Toast['action']) => show('info', message, action),
  warning: (message: string, action?: Toast['action']) => show('warning', message, action),
  /** Never `alert()`. An error is a designed surface, not a browser dialog. */
  danger: (message: string, action?: Toast['action']) => show('danger', message, action),
  dismissAll: () => {
    for (const timer of timers.values()) window.clearTimeout(timer);
    timers.clear();
    toasts = [];
    publish();
  },
};
